import React, {useEffect, useState} from 'react';
import {Modal, Row, Col} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';
import {Form as FormRender} from 'formik';
import {GreenButton, LabelField, StyledH3, WhiteButton} from '@styled';
import {
  Input,
  Form,
  DropdownField,
  MainLoader,
  MaskedInput,
  Checkbox,
} from '@components';
import {
  ENTITY_TYPE,
  PHONE_DIAL_CODE,
  RELATION_TO_PARENT,
} from '@app/utils/constants';
import {useDispatch, useSelector} from 'react-redux';
import {get} from 'lodash';
import {useHistory} from 'react-router-dom';
import {sagaActions} from './redux/saga';
import {resetFormSubmitSuccess} from './redux/index';

const AddEntityModal = ({show, toggleModal}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const isLoading = useSelector((state) => state.entity.formLoading);
  const parentEntities = useSelector((state) => state.entity.parentEntities);
  const formSubmitSuccess = useSelector(
    (state) => state.entity.formSubmitSuccess
  );
  const user = useSelector((state) => state.auth.currentUser);

  const [entityType, setEntityType] = useState('');
  const [assignMeReporter, setAssignMeReporter] = useState(false);

  useEffect(() => {
    if (show) {
      setAssignMeReporter(false);
    }
  }, [show]);

  useEffect(() => {
    if (entityType) {
      dispatch({
        type: sagaActions.FETCH_PARENT_ENTITIES,
        payload: {values: {entityType: entityType.value}},
      });
    }
  }, [entityType]);

  useEffect(() => {
    if (formSubmitSuccess) {
      dispatch(resetFormSubmitSuccess({formName: 'entity', flagValue: false}));
      toggleModal(false);
    }
  }, [formSubmitSuccess]);

  const handleSubmit = (values) => {
    dispatch({type: sagaActions.CREATE_ENTITY, payload: {values, history}});
  };

  return (
    <Modal
      show={show}
      onHide={() => toggleModal(false)}
      centered
      scrollable
      keyboard={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <h6>{t('reporter.entity.addNewEntity')}</h6>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <MainLoader
            minHeight="100vh"
            spinnerColor="#479541"
            background="rgba(0,0,0,0.2)"
          />
        )}
        <Form
          initialValues={{
            entityType: '',
            parentEntity: '',
            relationToParentEntity: '',
            entityName: '',
            reporterName: '',
            reporterEmail: '',
            reporterPhone: '',
            assignMeReporter: [],
          }}
          validationSchema={Yup.object({
            entityType: Yup.object().required(
              t('reporter.entity.required.entityType')
            ),
            parentEntity: Yup.object().required(
              t('reporter.entity.required.parentEntity')
            ),
            relationToParentEntity: Yup.object().required(
              t('reporter.entity.required.relationToParentEntity')
            ),
            entityName: Yup.string().required(
              t('reporter.entity.required.entityName')
            ),
            reporterName: Yup.string().when('assignMeReporter', {
              is: (cc) => cc.length === 0,
              then: Yup.string().required(
                t('reporter.entity.required.reporterName')
              ),
            }),
            reporterEmail: Yup.string().when('assignMeReporter', {
              is: (cc) => cc.length === 0,
              then: Yup.string()
                .required(t('reporter.entity.required.reporterEmail'))
                .email(t('reporter.entity.required.invalidEmail')),
            }),
            reporterPhone: Yup.string().when('assignMeReporter', {
              is: (cc) => cc.length === 0,
              then: Yup.string()
                .required(t('reporter.entity.required.reporterPhone'))
                .test({
                  name: 'is-valid-phone',
                  message: t('reporter.entity.required.invalidPhone'),
                  test: (value) => {
                    let valid = value && value.indexOf('_') === -1;
                    if (valid) {
                      valid = Number(value) > 0;
                    }
                    return valid;
                  },
                }),
            }),
          })}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          {({setFieldValue}) => {
            return (
              <FormRender noValidate className="reactForm">
                <Row className="mb-2">
                  <Col>
                    <DropdownField
                      id="entityType"
                      name="entityType"
                      label={t('reporter.entity.entityType')}
                      options={ENTITY_TYPE.map((item) => ({
                        label: item,
                        value: item,
                      }))}
                      placeholder=""
                      required
                      menuPortalTarget="parent"
                      onChange={(val) => {
                        setEntityType(val);
                      }}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <DropdownField
                      id="parentEntity"
                      name="parentEntity"
                      label={t('reporter.entity.parentEntity')}
                      options={parentEntities}
                      placeholder=""
                      required
                      menuPortalTarget="parent"
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <DropdownField
                      id="relationToParentEntity"
                      name="relationToParentEntity"
                      label={t('reporter.entity.relationToParentEntity')}
                      options={RELATION_TO_PARENT}
                      placeholder=""
                      required
                      menuPortalTarget="parent"
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Input
                      type="text"
                      name="entityName"
                      id="entityName"
                      label={t('reporter.entity.entityName')}
                      placeholder=""
                      required
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <StyledH3>{t('reporter.entity.reporterTitle')}</StyledH3>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Checkbox
                      id="assignMeReporter"
                      name="assignMeReporter"
                      label={t('reporter.entity.assignMeReporter')}
                      fieldValue="1"
                      onChange={(evt) => {
                        const isChecked = evt.target.checked;
                        setAssignMeReporter(isChecked);
                        let reporterName = '';
                        let reporterEmail = '';
                        let reporterPhone = '';
                        if (isChecked) {
                          reporterName = get(user, 'Name', '');
                          reporterEmail = get(user, 'Email', '');
                          reporterPhone = get(user, 'Phone', '').replace(
                            PHONE_DIAL_CODE,
                            ''
                          );
                        }
                        setFieldValue('reporterName', reporterName);
                        setFieldValue('reporterEmail', reporterEmail);
                        setFieldValue('reporterPhone', reporterPhone);
                      }}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Input
                      type="text"
                      name="reporterName"
                      id="reporterName"
                      label={t('reporter.entity.reporterName')}
                      placeholder=""
                      required
                      disabled={assignMeReporter}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Input
                      type="text"
                      name="reporterEmail"
                      id="reporterEmail"
                      label={t('reporter.entity.reporterEmail')}
                      placeholder=""
                      required
                      disabled={assignMeReporter}
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Row noGutters>
                      <Col xs={3} lg={2} className="text-right mt-4">
                        <LabelField className="mb-1 mt-1 mr-2 text-nowrap">
                          {t('reporter.entity.phoneDialCode')}
                        </LabelField>
                      </Col>
                      <Col xs={9} lg={10}>
                        <MaskedInput
                          mask="999999999"
                          name="reporterPhone"
                          id="ReporterPhone"
                          label={t('reporter.entity.reporterPhone')}
                          placeholder=""
                          required
                          disabled={assignMeReporter}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row
                  className="mt-4"
                  style={{
                    visibility: 'hidden',
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    padding: 0,
                    margin: 0,
                    fontSize: 0,
                  }}
                >
                  <Col>
                    <GreenButton id="mSubmitBtn" type="submit" className="mr-1">
                      {t('reporter.entity.addEntitySubmitLabel')}
                    </GreenButton>
                  </Col>
                </Row>
              </FormRender>
            );
          }}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <GreenButton
          type="button"
          className="mr-1"
          onClick={() => {
            document.getElementById('mSubmitBtn').click();
          }}
        >
          {t('reporter.entity.addEntitySubmitLabel')}
        </GreenButton>
        <WhiteButton
          type="button"
          className="ml-1"
          onClick={() => toggleModal(false)}
        >
          {t('reporter.entity.addEntityCancelLabel')}
        </WhiteButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEntityModal;
